import React, { useEffect, useMemo, useState } from "react";
import {
  ProSidebar,
  Menu,
  MenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
  SubMenu,
} from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import {
  FiHome,
  FiUsers,
  FiLogOut,
  FiCalendar,
  FiFileText,
} from "react-icons/fi";
import { BiUserCircle } from "react-icons/bi";
import { TbHotelService } from "react-icons/tb";
import { MdOutlineRateReview, MdOutlineSchedule } from "react-icons/md";
import {
  AiOutlineUserAdd,
  AiOutlineFieldTime,
  AiOutlineSetting,
  AiOutlineClose,
} from "react-icons/ai";
import { PiListStarFill } from "react-icons/pi";
import { FaBlog, FaBell, FaFileInvoice } from "react-icons/fa";
import "react-pro-sidebar/dist/css/styles.css";
import "./Sidebar.css";
import Icon01 from "../../assets/images/indent-decrease.svg";
import Icon02 from "../../assets/images/indent-increase.svg";
import Logo01 from "../../assets/images/logo/Logo.png";
import Logo02 from "../../assets/images/logo/popuplogo.png";
import { logout, getnotificationapi, updatebookingapi, canAccessFsrModule, getCurrentUser } from "../../Api/auth";
import { Button, Modal } from "react-bootstrap";
import ChildComponent from "../../Bookingdata/Appointment";
import { BsPinAngle, BsPinAngleFill } from "react-icons/bs";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import {
  getSidebarNavConfig,
  flattenNavItems,
  SIDEBAR_SECTIONS,
} from "./sidebarNavConfig";
import { useSidebarPreferences } from "./useSidebarPreferences";

import { notify } from "../../utils/notify";

const getUserStorageKey = (user) => {
  if (!user) return "guest";
  return String(user.id || user.user_id || user.email || "guest");
};

const SidebarNavLink = ({
  item,
  isActive,
  isPinned,
  onTogglePin,
  onNavigate,
  showPin,
}) => (
  <div className={`sidebar-nav-item ${isActive ? "sidebar-nav-item-active" : ""}`}>
    <Link to={item.path} className="side-nav-link" onClick={onNavigate}>
      <MenuItem className={isActive ? "active" : ""} icon={item.icon}>
        {item.label}
      </MenuItem>
    </Link>
    {showPin ? (
      <button
        type="button"
        className={`sidebar-pin-btn ${isPinned ? "pinned" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin(item.id);
        }}
        aria-label={isPinned ? "Unpin module" : "Pin module"}
        title={isPinned ? "Unpin" : "Pin to top"}
      >
        {isPinned ? <BsPinAngleFill /> : <BsPinAngle />}
      </button>
    ) : null}
  </div>
);

const Sidebar = () => {
  const [openpopup, setOpenPopup] = useState(true);
  const [show, setShow] = useState(false);
  const [menuCollapse, setMenuCollapse] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const splitLocation = pathname.split("/");

  const canAccessFsr = canAccessFsrModule();
  const currentUser = getCurrentUser();
  const userKey = getUserStorageKey(currentUser);
  const navConfig = useMemo(
    () => getSidebarNavConfig({ canAccessFsr }),
    [canAccessFsr]
  );
  const navSections = navConfig.sections;
  const standaloneItems = navConfig.standalone || [];
  const allNavItems = flattenNavItems(navConfig);
  const {
    pinnedIds,
    togglePin,
    toggleSection,
    expandSection,
    isPinned,
    isSectionOpen,
  } = useSidebarPreferences(userKey, navSections);

  const pinnedItems = allNavItems.filter((item) => pinnedIds.includes(item.id));

  const isItemActive = (item) => splitLocation[1] === item.pathMatch;

  const sectionHasActiveItem = (section) =>
    section.items.some(
      (item) => item.visible !== false && isItemActive(item)
    );

  useEffect(() => {
    navSections.forEach((section) => {
      if (section.collapsible && sectionHasActiveItem(section)) {
        expandSection(section.id);
      }
    });
  }, [pathname, expandSection, navSections]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [notification, setNotification] = useState([]);
  const [bookingid, setBookingId] = useState(0);
  const [oldbookingid, setOldBookingId] = useState(0);

  const [notificationId, setNotificationId] = useState(0);

  const [collapse, setCollapse] = useState(false);
  const toggle = () => {
    document
      .querySelector(".main-container")
      .classList.toggle("new-class-name");
    setCollapse(!collapse);
  };

  const addClass = () => {
    if (collapse) {
      document.querySelector(".sidebar-container").classList.add("class-none");
    } else {
      document
        .querySelector(".sidebar-container")
        .classList.remove("new-class-name");
      document.querySelector(".stop").classList.remove("new-class-name");
    }
  };

  const classRemove = () => {
    document.querySelector(".stop").classList.remove("new-class-name");
  };

  const renderNavItem = (item) => {
    if (item.visible === false) return null;
    return (
      <SidebarNavLink
        key={item.id}
        item={item}
        isActive={isItemActive(item)}
        isPinned={isPinned(item.id)}
        onTogglePin={togglePin}
        onNavigate={classRemove}
        showPin={!menuCollapse}
      />
    );
  };

  const renderCollapsibleSection = (section) => {
    const visibleItems = section.items.filter((item) => item.visible !== false);
    if (!visibleItems.length) return null;

    const isOpen = isSectionOpen(section.id, section.defaultOpen);
    const hasActive = sectionHasActiveItem(section);

    return (
      <div
        key={section.id}
        className={`sidebar-section ${hasActive ? "sidebar-section-active" : ""}`}
      >
        <button
          type="button"
          className="sidebar-section-header"
          onClick={() => toggleSection(section.id)}
          aria-expanded={isOpen}
        >
          <span className="sidebar-section-title">{section.label}</span>
          <span className="sidebar-section-chevron">
            {isOpen ? <HiChevronDown /> : <HiChevronRight />}
          </span>
        </button>
        {isOpen ? (
          <div className="sidebar-section-items">
            {visibleItems.map((item) => renderNavItem(item))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderPinnedSection = () => {
    if (!pinnedItems.length || menuCollapse) return null;

    const isOpen = isSectionOpen(SIDEBAR_SECTIONS.PINNED, true);

    return (
      <div className="sidebar-section sidebar-section-pinned">
        <button
          type="button"
          className="sidebar-section-header"
          onClick={() => toggleSection(SIDEBAR_SECTIONS.PINNED)}
          aria-expanded={isOpen}
        >
          <span className="sidebar-section-title">Pinned</span>
          <span className="sidebar-section-chevron">
            {isOpen ? <HiChevronDown /> : <HiChevronRight />}
          </span>
        </button>
        {isOpen ? (
          <div className="sidebar-section-items">
            {pinnedItems.map((item) => renderNavItem(item))}
          </div>
        ) : null}
      </div>
    );
  };

  // const location = useLocation();

  // //destructuring pathname from location
  // const { pathname } = location;

  // //Javascript split method to get the name of the path in array
  // const splitLocation = pathname.split("/");

  // const [menuCollapse, setMenuCollapse] = useState(false);
  // const menuIconClick = () => {
  //   menuCollapse ? setMenuCollapse(false) : setMenuCollapse(true);
  // };

  // useEffect(() => {
  //   // if (location.pathname === '/appointment') {
  //   //   setMenuCollapse(true);
  //   //   document.querySelector(".main-container").classList.toggle("new-class-name");
  //   // }
  //   const mainContainer = document.querySelector(".main-container");
  //   if (pathname === '/appointment') {
  //     setMenuCollapse(true);
  //     if (!mainContainer.classList.contains("new-class-name")) {
  //       mainContainer.classList.add("new-class-name");
  //     }
  //   } else {
  //     mainContainer.classList.remove("new-class-name");
  //   }
  // }, [location]);

  const menuIconClick = () => {
    setMenuCollapse(!menuCollapse);
  };

  useEffect(() => {
    const mainContainer = document.querySelector(".main-container");
    if (pathname === "/appointment") {
      setMenuCollapse(true);
      if (!mainContainer.classList.contains("new-class-name")) {
        mainContainer.classList.add("new-class-name");
      }
    } else {
      mainContainer.classList.remove("new-class-name");
    }
  }, [pathname]);

  const getnotifications = async () => {
    getnotificationapi()
      .then((res) => {
        if (res.code === 200) {
          const ApiResponse = res.data;
          setNotification(ApiResponse.data);
        } else {
          notify.error("Something went wrong");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  function getTimeAgo(datetimeString) {
    if (datetimeString) {
      const bookedDate = new Date(datetimeString);
      const currentDate = new Date();
      const differenceInMilliseconds = currentDate - bookedDate;

      // Convert milliseconds to minutes, hours, etc.
      const seconds = Math.floor(differenceInMilliseconds / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      let result = "";
      if (days > 0) {
        result += `${days} days `;
      }
      if (hours % 24 > 0) {
        result += `${hours % 24} hours `;
      }
      if (minutes % 60 > 0) {
        result += `${minutes % 60} minutes `;
      }
      if (seconds % 60 > 0) {
        result += `${seconds % 60} seconds `;
      }

      return result.trim() + " ago" || "Just now";
    } else {
      return "";
    }
  }

  const updatebooking = async (id) => {
    setNotificationId(id);
    const formData = new FormData();
    formData.append("order_id", id);
    updatebookingapi(formData)
      .then((response) => {
        if (response.code === 200) {
          getnotifications();
        } else {
          notify.error("Something went wrong");
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setNotificationId(0);
        // handleClose();
      });
  };

  useEffect(() => {
    getnotifications();
  }, []);

  const callChildFunction = (booking_id) => {
    setBookingId(booking_id);
  };

  useEffect(() => {
    if (bookingid !== 0) {
      updatebooking(oldbookingid);
    }
  }, [bookingid]);

  useEffect(() => {
    if (openpopup === false) {
      handleClose();
    }
  }, [openpopup]);

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="notification-modal"
      >
        <Modal.Body>
          <div className="notification-modal-header">
            <div className="notification-modal-title">
              <span className="notification-modal-icon">
                <FaBell />
              </span>
              <div>
                <h5>Notifications</h5>
                {/* <span className="notification-modal-subtitle">
                  {notification?.length > 0
                    ? `${notification.length} unread update${notification.length > 1 ? "s" : ""}`
                    : "You're all caught up"}
                </span> */}
              </div>
            </div>
            <button
              type="button"
              className="notification-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <AiOutlineClose />
            </button>
          </div>
          <div className="notification-modal-body">
            {notification && notification.length > 0 ? (
              notification.map((item, index) => (
                <div className="notification-card" key={item.order_id || index}>
                  <div className="notification-card-accent" />
                  <div className="notification-card-content">
                    <div className="notification-avatar">
                      <FiCalendar />
                    </div>
                    <div className="card-info">
                      <div className="card-text">
                        <h6 className="fw-normal">
                          Service Type: <b>{item.servicename}</b>
                          <br />
                          Customer Name: <b>{item.clientname}</b>
                          <br />
                          Technician: <b>{item.fullname}</b>
                          {/* {item.book_by == 2
                            ? `Booking ${item.servicename} is booked by technician ${item.fullname} for client ${item.clientname}`
                            : `Booking ${item.servicename} is booked by client ${item.clientname} and assigned to technician ${item.fullname}`} */}
                        </h6>
                        {item.book_date != "1990-01-01 00:00:00" && (
                          <p>
                            <AiOutlineFieldTime />
                            {getTimeAgo(item.book_date)}
                          </p>
                        )}
                      </div>
                      <div className="notification-card-actions">
                        <button
                          className="notification-btn notification-btn-view"
                          onClick={() => {
                            callChildFunction(item.order_id);
                          }}
                        >
                          View
                        </button>
                        <button
                          className="notification-btn notification-btn-read"
                          onClick={() => updatebooking(item.order_id)}
                          disabled={item.order_id === notificationId}
                        >
                          {item.order_id === notificationId
                            ? "Processing..."
                            : "Mark Read"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="notification-empty">
                <span className="notification-empty-icon">
                  <FaBell />
                </span>
                <h6>No notifications yet</h6>
                <p>New booking alerts will show up here</p>
              </div>
            )}
          </div>
          {notification && notification.length > 0 && (
            <div className="notification-modal-footer">
              <Link to="/allnotification" className="view-all-btn" onClick={handleClose}>
                View all notifications
              </Link>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <div id="header">
        {/* collapsed props to change menu size using menucollapse state */}
        <ProSidebar collapsed={menuCollapse}>
          <SidebarHeader>
            <div className="logotext">
              {/* small and big change using menucollapse state */}
              {menuCollapse ? (
                <img src={Logo02} alt="logo" className="logo02" />
              ) : (
                <img src={Logo01} alt="logo" className="logo01" />
              )}
            </div>
            <div onClick={toggle}>
              <div className="closemenu" onClick={menuIconClick}>
                {/* changing menu collapse icon on click */}
                {menuCollapse ? (
                  <img src={Icon02} alt="Icon" />
                ) : (
                  <img src={Icon01} alt="Icon" />
                )}
              </div>
            </div>

            <div className="closemenu-mobile" onClick={addClass}>
              <AiOutlineClose />
            </div>
          </SidebarHeader>
          <SidebarContent className="sidebar-content-sidebar">
            <Menu iconShape="square">
              <div className="sidebar-nav-root">
                {standaloneItems.map((item) => renderNavItem(item))}
                {renderPinnedSection()}
                {navSections.map((section) => renderCollapsibleSection(section))}
              </div>
            </Menu>
          </SidebarContent>
          <SidebarFooter>
            <div className="px-4">
              {currentUser?.fullname || ''}
            </div>
            <Menu iconShape="square">
              <Button onClick={logout} className="logout-btn">
                <MenuItem icon={<FiLogOut />}>Logout</MenuItem>
              </Button>
              <Button
                onClick={() => {
                  getnotifications();
                  handleShow();
                }}
                className="notification_btn"
              >
                <MenuItem>
                  <FaBell />
                  {notification.length > 0 && (
                    <span class="icon-button__badge">
                      {notification.length}
                    </span>
                  )}
                </MenuItem>
              </Button>
              {/* <div className="custom-notification-container">
                <div className="custom-notification">
                  <div className="notification-box">
                    <div className="notification-header">
                      <h3>Notification</h3>
                    </div>
                  </div>
                </div>
              </div> */}
            </Menu>
          </SidebarFooter>
        </ProSidebar>
        {bookingid != 0 ? (
          <ChildComponent
            order_id={bookingid}
            onCloseidChangebooking={(editbookingid) =>
              setBookingId(editbookingid)
            }
            onCloseidChangeoldbookingid={(oldbookingid) =>
              setOldBookingId(oldbookingid)
            }
            onclosepopup={(popup) => setOpenPopup(popup)}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default Sidebar;
